/**
 * resultsReportGenerator.ts
 * Seriöses, druckoptimiertes PDF der Ergebnisseite.
 * Layout: Dunkelblau / Weiß / Grau — aufgeräumt, professionell.
 * Alle Blöcke der ResultsMaster-Seite werden 1:1 übernommen.
 */

import type { MasterResult } from '@/lib/master-engine';

// ─── Helpers ───────────────────────────────────────────────────────────────────
function fmtEur(v: number | null | undefined): string {
  if (!Number.isFinite(Number(v)) || !v) return '0 €';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
  }).format(Number(v));
}
function fmtPct(v: number | null | undefined, d = 1): string {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return '–';
  return Number(v).toFixed(d).replace('.', ',') + '%';
}
function fmtNum(v: number | null | undefined, d = 0): string {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return '–';
  return Number(v).toFixed(d).replace('.', ',');
}
function today(): string {
  return new Date().toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });
}
function safe(v: string | null | undefined, fallback = '–'): string {
  return (v && String(v).trim()) ? String(v) : fallback;
}
function safeArr<T>(arr: T[] | null | undefined): T[] {
  return Array.isArray(arr) ? arr : [];
}

// ─── Styles ────────────────────────────────────────────────────────────────────
function buildStyles(): string {
  return `
    @page { size: A4 landscape; margin: 0; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
      font-size: 10.5pt; color: #0f172a; line-height: 1.6; background: #f1f5f9;
      word-wrap: break-word; overflow-wrap: break-word;
    }
    @media print {
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      body { margin: 0; padding: 0; background: #fff; }
      .no-print { display: none !important; }
    }

    /* Slide Container */
    .slide {
      width: 297mm;
      min-height: 210mm;
      background: #fff;
      margin: 0 auto 20px;
      position: relative;
      page-break-after: always;
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    }
    @media print {
      .slide { margin: 0; box-shadow: none; page-break-after: always; height: auto; }
    }
    .slide-content {
      padding: 15mm 20mm;
      min-height: 100%;
      display: block;
    }
    .page-break {
      page-break-before: always;
      break-before: page;
    }

    /* Toolbar */
    .toolbar {
      position: fixed; top: 0; left: 0; right: 0; z-index: 999;
      background: #0f172a; padding: 10px 24px;
      display: flex; align-items: center; justify-content: space-between;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    }
    .toolbar-label { color: rgba(255,255,255,0.65); font-size: 12px; font-weight: 600; }
    .toolbar-btns { display: flex; gap: 10px; }
    .btn-pdf {
      background: #1d4ed8; color: #fff; border: none;
      padding: 8px 18px; border-radius: 7px; font-size: 12px; font-weight: 700; cursor: pointer;
    }
    .btn-close {
      background: #475569; color: #fff; border: none;
      padding: 8px 14px; border-radius: 7px; font-size: 12px; font-weight: 700; cursor: pointer;
    }
    .toolbar-spacer { height: 50px; }

    /* ── Cover ── */
    .cover {
      width: 297mm;
      height: 210mm;
      background: linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #1d4ed8 100%);
      color: #fff; padding: 50px 48px 40px; position: relative; overflow: hidden;
      page-break-after: always;
      display: flex; flex-direction: column; justify-content: center;
      margin: 0 auto 20px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    }
    @media print {
      .cover { margin: 0; box-shadow: none; }
    }
    .cover::before {
      content: ''; position: absolute; top: -80px; right: -80px;
      width: 320px; height: 320px; border-radius: 50%; background: rgba(59,130,246,0.12);
    }
    .cover::after {
      content: ''; position: absolute; bottom: -60px; left: -60px;
      width: 240px; height: 240px; border-radius: 50%; background: rgba(16,185,129,0.08);
    }
    .cover-logo {
      font-size: 10pt; font-weight: 900; letter-spacing: 6px;
      color: rgba(255,255,255,0.35); margin-bottom: 48px; text-transform: uppercase;
    }
    .cover-badge {
      display: inline-flex; align-items: center; gap: 7px;
      background: rgba(16,185,129,0.15); border: 1px solid rgba(16,185,129,0.3);
      padding: 5px 14px; border-radius: 999px;
      font-size: 7.5pt; font-weight: 700; color: #6ee7b7;
      text-transform: uppercase; letter-spacing: 1px; margin-bottom: 20px;
    }
    .cover h1 { font-size: 26pt; color: #fff; line-height: 1.15; margin-bottom: 8px; font-weight: 900; }
    .cover-sub { font-size: 11pt; color: rgba(255,255,255,0.55); margin-bottom: 36px; }
    .cover-meta { font-size: 8.5pt; color: rgba(255,255,255,0.3); margin-top: 8px; }
    .cover-kpi-row {
      display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-top: 32px;
    }
    .cover-kpi {
      background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1);
      border-radius: 12px; padding: 16px;
    }
    .cover-kpi-label {
      font-size: 7pt; font-weight: 700; text-transform: uppercase;
      letter-spacing: 1px; color: rgba(255,255,255,0.4); margin-bottom: 6px;
    }
    .cover-kpi-val { font-size: 18pt; font-weight: 900; color: #fff; line-height: 1; }
    .cover-kpi-val.accent { color: #34d399; }
    .cover-kpi-val.warn { color: #fb923c; }
    .cover-kpi-val.red { color: #f87171; }
    .cover-kpi-sub { font-size: 7.5pt; color: rgba(255,255,255,0.3); margin-top: 4px; }

    /* ── Dark section header ── */
    .dark-header {
      background: linear-gradient(135deg, #0f172a, #1e3a8a);
      color: #fff; padding: 22px 28px; border-radius: 14px;
      margin: 0 0 20px;
    }
    .dark-header-label {
      font-size: 7pt; font-weight: 700; letter-spacing: 2px;
      text-transform: uppercase; color: rgba(255,255,255,0.4); margin-bottom: 5px;
    }
    .dark-header h2 {
      font-size: 14pt; font-weight: 800; color: #fff; margin: 0;
    }
    .dark-header .sub { font-size: 9pt; color: rgba(255,255,255,0.5); margin-top: 4px; }

    /* ── KPI grids ── */
    .kpi-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 18px; }
    .kpi-grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 18px; }
    .kpi-grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 18px; }
    .kpi-card {
      border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 14px; background: #f8fafc;
    }
    .kpi-card.alert { border-color: #fca5a5; background: #fef2f2; }
    .kpi-card.ok    { border-color: #86efac; background: #f0fdf4; }
    .kpi-card.warn  { border-color: #fde68a; background: #fffbeb; }
    .kpi-label {
      font-size: 7pt; font-weight: 700; text-transform: uppercase;
      letter-spacing: 0.8px; color: #94a3b8; margin-bottom: 6px;
    }
    .kpi-val { font-size: 17pt; font-weight: 900; color: #0f172a; line-height: 1; }
    .kpi-val.red   { color: #ef4444; }
    .kpi-val.green { color: #10b981; }
    .kpi-val.blue  { color: #1d4ed8; }
    .kpi-val.amber { color: #f59e0b; }
    .kpi-val.sm    { font-size: 12pt; }
    .kpi-sub { font-size: 7.5pt; color: #94a3b8; margin-top: 4px; }

    /* ── Alert box ── */
    .alert-box {
      padding: 12px 16px; border-radius: 0 10px 10px 0; margin: 12px 0;
      font-size: 8.5pt; line-height: 1.55;
    }
    .alert-box.red   { background: #fef2f2; border-left: 4px solid #ef4444; color: #7f1d1d; }
    .alert-box.green { background: #f0fdf4; border-left: 4px solid #10b981; color: #14532d; }
    .alert-box.blue  { background: #eff6ff; border-left: 4px solid #3b82f6; color: #1e3a8a; }
    .alert-box.amber { background: #fffbeb; border-left: 4px solid #f59e0b; color: #78350f; }

    /* ── Lever cards ── */
    .lever-card {
      border: 1.5px solid #e2e8f0; border-radius: 12px; padding: 16px; margin-bottom: 12px;
      background: #fff;
    }
    .lever-card.rank-1 { border-color: #dc2626; border-left-width: 4px; }
    .lever-card.rank-2 { border-color: #d97706; border-left-width: 4px; }
    .lever-card.rank-3 { border-color: #059669; border-left-width: 4px; }
    .lever-rank {
      font-size: 7pt; font-weight: 700; text-transform: uppercase;
      letter-spacing: 1px; color: #1d4ed8; margin-bottom: 4px;
    }
    .lever-title { font-size: 10.5pt; font-weight: 800; color: #0f172a; margin-bottom: 5px; }
    .lever-why   { font-size: 8.5pt; color: #475569; line-height: 1.5; }
    .lever-impact {
      display: inline-block; margin-top: 8px;
      background: #dbeafe; color: #1d4ed8;
      padding: 3px 10px; border-radius: 999px; font-size: 7.5pt; font-weight: 700;
    }

    /* ── Cause cards ── */
    .cause-card { border-radius: 10px; padding: 14px; margin-bottom: 10px; }
    .cause-card.high   { background: #fef2f2; border: 1.5px solid #fca5a5; }
    .cause-card.medium { background: #fffbeb; border: 1.5px solid #fde68a; }
    .cause-card.good   { background: #f0fdf4; border: 1.5px solid #86efac; }
    .cause-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; flex-wrap: wrap; }
    .cause-title { font-size: 10pt; font-weight: 700; color: #0f172a; }
    .cause-sev {
      font-size: 7pt; font-weight: 700; padding: 3px 8px;
      border-radius: 999px; text-transform: uppercase; white-space: nowrap;
    }
    .sev-high   { background: #fecaca; color: #7f1d1d; }
    .sev-medium { background: #fef3c7; color: #78350f; }
    .sev-good   { background: #d1fae5; color: #065f46; }
    .cause-summary { font-size: 8.5pt; color: #334155; margin-top: 6px; line-height: 1.5; }
    .cause-impact  { font-size: 8.5pt; color: #64748b; margin-top: 4px; }

    /* ── Funnel table ── */
    table { width: 100%; border-collapse: collapse; margin: 12px 0; }
    th {
      background: #1e3a8a; color: #fff;
      padding: 8px 12px; text-align: left; font-size: 7.5pt; font-weight: 700;
    }
    td { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; font-size: 8.5pt; color: #334155; }
    tr:last-child td { border-bottom: none; }
    tr:nth-child(even) td { background: #f8fafc; }
    td.num { text-align: right; font-weight: 700; }
    td.bad  { color: #ef4444; font-weight: 700; }
    td.good { color: #10b981; font-weight: 700; }
    td.warn { color: #f59e0b; font-weight: 700; }

    /* ── Capacity block ── */
    .capacity-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin: 14px 0; }
    .cap-card { border-radius: 12px; padding: 16px; text-align: center; }
    .cap-card.current  { background: #f1f5f9; border: 1.5px solid #cbd5e1; }
    .cap-card.optimized{ background: #eff6ff; border: 1.5px solid #93c5fd; }
    .cap-card.target   { background: #fef2f2; border: 1.5px solid #fca5a5; }
    .cap-card.target.ok{ background: #f0fdf4; border-color: #86efac; }
    .cap-card-label { font-size: 7pt; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #64748b; margin-bottom: 6px; }
    .cap-card-val   { font-size: 16pt; font-weight: 900; color: #0f172a; line-height: 1; }
    .cap-card-sub   { font-size: 7.5pt; color: #64748b; margin-top: 4px; }

    /* ── Funnel path (goalPath) ── */
    .path-table { width: 100%; border-collapse: collapse; margin: 12px 0; }
    .path-table th { background: #1e3a8a; color: #fff; padding: 8px 12px; font-size: 7.5pt; font-weight: 700; text-align: left; }
    .path-table td { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; font-size: 8.5pt; }
    .path-table td.num { text-align: right; font-weight: 700; }
    .path-table tr:nth-child(even) td { background: #f8fafc; }

    /* ── Rep execution ── */
    .rep-row { display: flex; align-items: center; gap: 10px; padding: 10px 0; border-bottom: 1px solid #f1f5f9; }
    .rep-row:last-child { border-bottom: none; }
    .rep-label { font-size: 8.5pt; color: #475569; min-width: 130px; }
    .rep-bar-wrap { flex: 1; background: #e2e8f0; border-radius: 999px; height: 8px; overflow: hidden; }
    .rep-bar { height: 8px; border-radius: 999px; background: #1d4ed8; }
    .rep-bar.ok  { background: #10b981; }
    .rep-bar.warn{ background: #f59e0b; }
    .rep-bar.crit{ background: #ef4444; }
    .rep-val { font-size: 8.5pt; font-weight: 700; min-width: 50px; text-align: right; }

    /* ── Data gaps ── */
    .gap-pill {
      display: inline-flex; align-items: center; gap: 5px;
      background: #fef2f2; border: 1px solid #fca5a5;
      padding: 4px 12px; border-radius: 999px;
      font-size: 7.5pt; font-weight: 700; color: #7f1d1d; margin: 3px 4px 3px 0;
    }

    /* ── Footer ── */
    .report-footer {
      margin-top: 20px; padding-top: 14px;
      border-top: 1px solid #e2e8f0;
      font-size: 7.5pt; color: #94a3b8; text-align: center;
    }
    p { font-size: 8.5pt; color: #475569; line-height: 1.6; margin-bottom: 8px; }
  `;
}

// ─── Cover ─────────────────────────────────────────────────────────────────────
function buildCover(r: MasterResult): string {
  const nd = r.normalizedData;
  const tp = r.teamPerformance;
  const cm = r.computedMetrics;

  const annual  = tp?.totalRevenueTargetYear ?? 0;
  const gap     = tp?.projectedYearEndGap ?? 0;
  const pacing  = tp?.pacingVsPlanPct ?? tp?.absoluteGoalAttainmentPct ?? 0;
  const monthly = nd?.monthlyRevenue ?? 0;

  const pacingColor = pacing >= 95 ? 'accent' : pacing >= 75 ? 'warn' : 'red';

  return `
  <div class="cover">
    <div class="cover-logo">SALESFORGE</div>
    <div class="cover-badge">&#9679; Vertriebsdiagnose · Vertraulich</div>
    <h1>${safe(nd?.companyName, 'Ihr Unternehmen')}:<br>Vertriebsdiagnose</h1>
    <div class="cover-sub">Wo Ihr Vertrieb Umsatz liegen lässt — und welcher Hebel jetzt entscheidet.</div>
    <div class="cover-kpi-row">
      <div class="cover-kpi">
        <div class="cover-kpi-label">Jahresziel</div>
        <div class="cover-kpi-val">${fmtEur(annual)}</div>
        <div class="cover-kpi-sub">Definierter Umsatz-Target</div>
      </div>
      <div class="cover-kpi">
        <div class="cover-kpi-label">Forecast-Gap</div>
        <div class="cover-kpi-val ${gap > 0 ? 'red' : 'accent'}">${gap > 0 ? fmtEur(gap) : '✓ Im Plan'}</div>
        <div class="cover-kpi-sub">Projizierte Ziellücke (Jahresende)</div>
      </div>
      <div class="cover-kpi">
        <div class="cover-kpi-label">Jahres-Pacing</div>
        <div class="cover-kpi-val ${pacingColor}">${fmtPct(pacing)}</div>
        <div class="cover-kpi-sub">Linearer Sollverlauf</div>
      </div>
      <div class="cover-kpi">
        <div class="cover-kpi-label">Aktive Reps</div>
        <div class="cover-kpi-val">${fmtNum(nd?.teamSize ?? tp?.productiveReps ?? 0)}</div>
        <div class="cover-kpi-sub">Produktive Vertriebsmitarbeiter</div>
      </div>
    </div>
    <div class="cover-kpi-row" style="margin-top:16px;">
      <div class="cover-kpi">
        <div class="cover-kpi-label">Monatsumsatz (Ist)</div>
        <div class="cover-kpi-val">${fmtEur(monthly)}</div>
        <div class="cover-kpi-sub">Laufend erzielt</div>
      </div>
      <div class="cover-kpi">
        <div class="cover-kpi-label">Monatl. Umsatzlücke</div>
        <div class="cover-kpi-val ${(cm?.monthlyRevenueGap ?? 0) > 0 ? 'warn' : 'accent'}">${fmtEur(cm?.monthlyRevenueGap ?? 0)}</div>
        <div class="cover-kpi-sub">Identifiziertes Potenzial</div>
      </div>
      <div class="cover-kpi">
        <div class="cover-kpi-label">&#216; Deal-Größe</div>
        <div class="cover-kpi-val">${fmtEur(nd?.averageDealSize ?? 0)}</div>
        <div class="cover-kpi-sub">Durchschnittlicher Abschluss</div>
      </div>
      <div class="cover-kpi">
        <div class="cover-kpi-label">Sales Cycle</div>
        <div class="cover-kpi-val">${fmtNum(nd?.salesCycleMonths ?? 0, 1)} Mo.</div>
        <div class="cover-kpi-sub">Durchschn. Abschlusszeit</div>
      </div>
    </div>
    <div class="cover-meta">${today()} · ${safe(nd?.companyName, 'Ihr Unternehmen')} · Salesforge Sales Audit · Nur für interne Verwendung</div>
  </div>`;
}

// ─── Section 1: Business Reality ──────────────────────────────────────────────
function buildBusinessReality(r: MasterResult): string {
  const nd = r.normalizedData;
  const tp = r.teamPerformance;
  const cm = r.computedMetrics;

  const annual    = tp?.totalRevenueTargetYear ?? 0;
  const monthly   = nd?.monthlyRevenue ?? 0;
  const pacing    = tp?.pacingVsPlanPct ?? tp?.absoluteGoalAttainmentPct ?? 0;
  const gap       = tp?.projectedYearEndGap ?? 0;
  const ytd       = tp?.revenueAchievedYTD ?? 0;
  const paceGap   = tp?.paceGapToDate ?? 0;
  const mthGap    = cm?.monthlyRevenueGap ?? 0;
  const annGap    = cm?.annualRevenueGap ?? 0;

  const pacingClass = pacing >= 95 ? 'ok' : pacing >= 70 ? 'warn' : 'alert';

  return `
  <div class="dark-header">
    <div class="dark-header-label">Abschnitt 1</div>
    <h2>Business Reality — Wo Ihr Vertrieb heute steht</h2>
    <div class="sub">Aktuelle Umsatzrealität und Abstand zum Jahresziel im Überblick.</div>
  </div>

  <div class="kpi-grid-4">
    <div class="kpi-card">
      <div class="kpi-label">Jahresziel</div>
      <div class="kpi-val">${fmtEur(annual)}</div>
      <div class="kpi-sub">Definierter Target</div>
    </div>
    <div class="kpi-card">
      <div class="kpi-label">Monatsumsatz (Ist)</div>
      <div class="kpi-val">${fmtEur(monthly)}</div>
      <div class="kpi-sub">Aktuell erzielt</div>
    </div>
    <div class="kpi-card ${pacingClass}">
      <div class="kpi-label">Jahres-Pacing</div>
      <div class="kpi-val ${pacing < 80 ? 'red' : pacing >= 95 ? 'green' : 'amber'}">${fmtPct(pacing)}</div>
      <div class="kpi-sub">vs. linearem Sollverlauf</div>
    </div>
    <div class="kpi-card ${gap > 0 ? 'alert' : 'ok'}">
      <div class="kpi-label">Forecast-Gap</div>
      <div class="kpi-val ${gap > 0 ? 'red' : 'green'}">${fmtEur(gap)}</div>
      <div class="kpi-sub">Projizierte Ziellücke</div>
    </div>
  </div>

  <div class="kpi-grid-4">
    <div class="kpi-card">
      <div class="kpi-label">YTD-Umsatz</div>
      <div class="kpi-val sm">${fmtEur(ytd)}</div>
      <div class="kpi-sub">Bisher erzielt (YTD)</div>
    </div>
    <div class="kpi-card ${paceGap > 0 ? 'alert' : 'ok'}">
      <div class="kpi-label">Zielrückstand heute</div>
      <div class="kpi-val sm ${paceGap > 0 ? 'red' : 'green'}">${fmtEur(paceGap)}</div>
      <div class="kpi-sub">Soll vs. Ist bisher</div>
    </div>
    <div class="kpi-card ${mthGap > 0 ? 'warn' : 'ok'}">
      <div class="kpi-label">Monatl. Umsatzlücke</div>
      <div class="kpi-val sm ${mthGap > 0 ? 'amber' : 'green'}">${fmtEur(mthGap)}</div>
      <div class="kpi-sub">Conversion-Potenzial / Monat</div>
    </div>
    <div class="kpi-card">
      <div class="kpi-label">Jährliches Potenzial</div>
      <div class="kpi-val sm blue">${fmtEur(annGap > 0 ? annGap : mthGap * 12)}</div>
      <div class="kpi-sub">Bei Benchmark-Performance</div>
    </div>
  </div>

  ${gap > 0 ? `
  <div class="alert-box red">
    <strong>Kritische Zielabweichung:</strong> Bei unveränderter Pace fehlen zum Jahresende ${fmtEur(gap)}.
    Ohne gezielte Intervention ist das Jahresziel rechnerisch nicht erreichbar.
  </div>` : `
  <div class="alert-box green">
    <strong>Auf Kurs:</strong> Das Team liegt im Sollverlauf. Fokus auf Stabilisierung und Skalierung.
  </div>`}`;
}

// ─── Section 2: Strukturelle Ursachen ─────────────────────────────────────────
function buildStructuralCauses(r: MasterResult): string {
  const causes = safeArr(r.structuralCauses);
  if (causes.length === 0) return '';

  const items = causes.map((c: any) => {
    const sev = c.severity ?? 'medium';
    const cls = sev === 'high' ? 'high' : sev === 'good' ? 'good' : 'medium';
    const sevLabel = sev === 'high' ? 'Kritisch' : sev === 'good' ? 'Stark' : 'Mittel';
    const sevCls   = sev === 'high' ? 'sev-high' : sev === 'good' ? 'sev-good' : 'sev-medium';
    return `
    <div class="cause-card ${cls}">
      <div class="cause-header">
        <div class="cause-title">${safe(c.title)}</div>
        <div class="cause-sev ${sevCls}">${sevLabel}</div>
      </div>
      ${c.metricLabel && c.metricValue ? `<div style="font-size:8pt;color:#64748b;margin-top:3px;">${c.metricLabel}: <strong>${c.metricValue}</strong></div>` : ''}
      ${c.summary ? `<div class="cause-summary" style="margin-top:6px;">${safe(c.summary)}</div>` : ''}
      ${c.businessImpact ? `<div class="cause-impact" style="margin-top:5px;"><strong>Geschäftliche Folge:</strong> ${safe(c.businessImpact)}</div>` : ''}
      ${(c.revenueImpactMonthly || 0) > 0 ? `<div style="font-size:8pt;font-weight:700;color:#10b981;margin-top:4px;">~${fmtEur(c.revenueImpactMonthly)}/Monat Potenzial</div>` : ''}
    </div>`;
  }).join('');

  return `
  <div class="dark-header page-break">
    <div class="dark-header-label">Abschnitt 2</div>
    <h2>Strukturelle Ursachen — Warum das Ziel verfehlt wird</h2>
    <div class="sub">Diese Faktoren erklären strukturell, warum das Umsatzziel nicht stabil erreichbar ist.</div>
  </div>
  ${items}`;
}

// ─── Section 7: Top-Hebel (nach Zielpfad) ────────────────────────────────────
function buildTopLevers(r: MasterResult): string {
  const levers = safeArr(r.topLevers).slice(0, 5);
  if (levers.length === 0) return '';

  const rankCls = ['rank-1', 'rank-2', 'rank-3', '', ''];

  const items = levers.map((lv: any, i: number) => `
  <div class="lever-card ${rankCls[i] ?? ''}">
    <div class="lever-rank">Hebel #${i + 1}</div>
    <div class="lever-title">${safe(lv.title)}</div>
    <div class="lever-why">${safe(lv.whyItMatters)}</div>
    ${Number.isFinite(Number(lv.monthlyImpact)) && Number(lv.monthlyImpact) > 0
      ? `<div class="lever-impact">~${fmtEur(lv.monthlyImpact)} / Monat Potenzial</div>`
      : ''}
  </div>`).join('');

  return `
  <div class="dark-header">
    <div class="dark-header-label">Abschnitt 7</div>
    <h2>Identifizierte Wachstumshebel — Priorisiert nach Impact</h2>
    <div class="sub">Die wichtigsten strukturellen Engpässe mit dem höchsten Umsatzeinfluss.</div>
  </div>
  ${items}`;
}

// ─── Section 3: Performance & Pace ────────────────────────────────────────────
function buildPerformancePace(r: MasterResult): string {
  const tp = r.teamPerformance;
  if (!tp) return '';

  const pacing  = tp.pacingVsPlanPct ?? tp.absoluteGoalAttainmentPct ?? 0;
  const reps    = tp.productiveReps ?? r.normalizedData?.teamSize ?? 0;
  const repTarget = tp.targetPerRepMonth ?? 0;
  const repActual = tp.actualRevenuePerRepMonthPaced ?? 0;
  const repHealth = tp.repHealthScore ?? 0;
  const recovery  = tp.pacingRecoveryPotentialMonthly ?? 0;

  const healthColor = repHealth < 60 ? 'red' : repHealth < 80 ? 'amber' : 'green';
  const pacingColor = pacing < 80 ? 'red' : pacing < 95 ? 'amber' : 'green';

  const signals = tp.summarySignals;
  const criticalFlags: string[] = [];
  if (signals?.hasPacingProblem) criticalFlags.push('Team liegt unter dem linearen Sollverlauf');
  if (signals?.hasTargetBreakdownProblem) criticalFlags.push('Umsatzziel nicht konsequent auf Reps heruntergebrochen');
  if (signals?.hasPipelineProblem) criticalFlags.push('Einzelne Reps haben unzureichende Pipeline-Coverage');
  if (signals?.hasVarianceProblem) criticalFlags.push('Hohe Leistungsstreuung im Team — Top vs. Median');
  if (signals?.hasReviewProblem) criticalFlags.push('Review-Rhythmus oder Steuerungsdisziplin als Risiko identifiziert');

  return `
  <div class="dark-header page-break">
    <div class="dark-header-label">Abschnitt 3</div>
    <h2>Performance &amp; Pace — Team-Analyse</h2>
    <div class="sub">Umsatz pro Rep, Kapazitätsabdeckung und Pacing-Analyse im Detail.</div>
  </div>

  <div class="kpi-grid-4">
    <div class="kpi-card">
      <div class="kpi-label">Aktive Reps</div>
      <div class="kpi-val">${fmtNum(reps)}</div>
      <div class="kpi-sub">Produktive Vertriebsmitarbeiter</div>
    </div>
    <div class="kpi-card ${pacing < 80 ? 'alert' : pacing >= 95 ? 'ok' : 'warn'}">
      <div class="kpi-label">Team-Pacing</div>
      <div class="kpi-val ${pacingColor}">${fmtPct(pacing)}</div>
      <div class="kpi-sub">Linearer Sollverlauf</div>
    </div>
    <div class="kpi-card ${repActual < repTarget * 0.85 ? 'alert' : repActual >= repTarget ? 'ok' : 'warn'}">
      <div class="kpi-label">Ø Umsatz / Rep / Monat (Ist)</div>
      <div class="kpi-val sm ${repActual < repTarget * 0.85 ? 'red' : ''}">${fmtEur(repActual)}</div>
      <div class="kpi-sub">Hochgerechneter Pace-Durchschnitt</div>
    </div>
    <div class="kpi-card">
      <div class="kpi-label">Ø Umsatz / Rep / Monat (Soll)</div>
      <div class="kpi-val sm">${fmtEur(repTarget)}</div>
      <div class="kpi-sub">Benötigt für Zielerreichung</div>
    </div>
  </div>

  <div class="kpi-grid-3">
    <div class="kpi-card">
      <div class="kpi-label">Rep Health Score</div>
      <div class="kpi-val ${healthColor}">${repHealth}/100</div>
      <div class="kpi-sub">Ziel, Pipeline &amp; Execution</div>
    </div>
    <div class="kpi-card ${recovery > 0 ? 'warn' : 'ok'}">
      <div class="kpi-label">Monatl. Aufholbedarf</div>
      <div class="kpi-val sm ${recovery > 0 ? 'amber' : 'green'}">${fmtEur(recovery)}</div>
      <div class="kpi-sub">Nötig für Jahresziel</div>
    </div>
    <div class="kpi-card">
      <div class="kpi-label">Projiziertes Jahresende</div>
      <div class="kpi-val sm">${fmtEur(tp.projectedYearEndRevenue ?? 0)}</div>
      <div class="kpi-sub">Bei unveränderter Pace</div>
    </div>
  </div>

  ${criticalFlags.length > 0 ? `
  <div style="background:#f8fafc;border:1.5px solid #e2e8f0;border-radius:12px;padding:16px;margin:12px 0;">
    <div style="font-size:8pt;font-weight:700;color:#0f172a;text-transform:uppercase;letter-spacing:1px;margin-bottom:10px;">Kritische Signale</div>
    ${criticalFlags.map(f => `
    <div style="display:flex;gap:10px;padding:6px 0;border-bottom:1px solid #f1f5f9;align-items:flex-start;">
      <span style="color:#ef4444;font-size:10pt;flex-shrink:0;margin-top:1px;">&#9888;</span>
      <span style="font-size:8.5pt;color:#334155;">${f}</span>
    </div>`).join('')}
  </div>` : ''}`;
}

// ─── Section 5: Kapazitäts-Check ──────────────────────────────────────────────
function buildCapacityCheck(r: MasterResult): string {
  const cm = r.computedMetrics;
  const tp = r.teamPerformance;
  if (!cm?.capacityCheck || !tp?.totalRevenueTargetYear) return '';

  const cc = cm.capacityCheck;
  const isUnreachable = cc.isTargetUnreachableWithCurrentSetup;
  const isHard = cc.isTargetUnreachableWithCurrentPerformance;
  const label = isUnreachable
    ? '🚨 Ziel mit aktuellem Setup nicht erreichbar'
    : isHard
    ? '⚠️ Ziel nur mit deutlich verbesserter Execution erreichbar'
    : '✅ Setup ist grundsätzlich zielkonform';

  const bgColor = isUnreachable ? '#fef2f2' : isHard ? '#fffbeb' : '#f0fdf4';
  const bdrColor = isUnreachable ? '#fca5a5' : isHard ? '#fcd34d' : '#86efac';
  const txtColor = isUnreachable ? '#991b1b' : isHard ? '#92400e' : '#14532d';

  return `
  <div class="dark-header">
    <div class="dark-header-label">Abschnitt 4</div>
    <h2>Kapazitäts-Check — Setup vs. Ziel</h2>
    <div class="sub">Theoretische Jahreskapazität des Teams im Vergleich zum definierten Umsatzziel.</div>
  </div>

  <div style="background:${bgColor};border:2px solid ${bdrColor};border-radius:14px;padding:20px;margin-bottom:16px;">
    <div style="font-size:10.5pt;font-weight:800;color:${txtColor};margin-bottom:14px;">${label}</div>
    <div class="capacity-grid">
      <div class="cap-card current">
        <div class="cap-card-label">Aktuelle Max-Kapazität</div>
        <div class="cap-card-val">${fmtEur(cc.currentMaxRevenueCapacity)}</div>
        <div class="cap-card-sub">Bei heutiger Performance</div>
      </div>
      <div class="cap-card optimized">
        <div class="cap-card-label">Optimierte Max-Kapazität</div>
        <div class="cap-card-val">${fmtEur(cc.optimizedMaxRevenueCapacity)}</div>
        <div class="cap-card-sub">Bei realistischer Optimierung</div>
      </div>
      <div class="cap-card target ${!isUnreachable ? 'ok' : ''}">
        <div class="cap-card-label">Jahresziel</div>
        <div class="cap-card-val">${fmtEur(tp.totalRevenueTargetYear)}</div>
        <div class="cap-card-sub">Definierter Target</div>
      </div>
    </div>
    ${cc.capacityGap > 0 ? `<div style="font-size:9pt;margin-top:10px;color:#64748b;">Kapazitätslücke: <strong style="color:#dc2626;">${fmtEur(cc.capacityGap)}</strong> — ohne Setup-Erweiterung strukturell nicht schließbar.</div>` : ''}
    ${!isUnreachable && !isHard ? `<div style="font-size:9pt;margin-top:10px;color:#14532d;">Setup ist zielkonform. Die Herausforderung liegt in Conversion-Konstanz, Pipeline-Qualität und sauberer Steuerung.</div>` : ''}
  </div>`;
}

// ─── Section 6: Funnel Conversion-Raten ───────────────────────────────────────
function buildFunnelConversions(r: MasterResult): string {
  const cm = r.computedMetrics;
  if (!cm) return '';

  const c2d = cm.convContactToDiscovery ?? 0;
  const d2p = cm.convDiscoveryToPresentation ?? 0;
  const p2d = cm.convPresentationToDeal ?? 0;

  const benchC2D = cm.benchmarkContactToDiscovery ?? 6;
  const benchD2P = cm.benchmarkDiscoveryToPresentation ?? 50;
  const benchP2D = cm.benchmarkPresentationToDeal ?? 28;

  const fd = cm.funnelDiagnosis;
  const bottleneck = fd?.stages?.find((s: any) => s.isBlocking)?.name ?? '';

  function rateClass(actual: number, bench: number): string {
    if (actual === 0) return 'warn';
    if (actual < bench * 0.7) return 'bad';
    if (actual >= bench) return 'good';
    return '';
  }
  function rateLabel(actual: number, bench: number): string {
    if (actual === 0) return 'Nicht erfasst';
    if (actual < bench * 0.7) return '⚠ Unter Benchmark';
    if (actual >= bench * 1.3) return '✓ Über Benchmark';
    if (actual >= bench) return '✓ Im Benchmark';
    return '~ Leichte Lücke';
  }

  return `
  <div class="dark-header page-break">
    <div class="dark-header-label">Abschnitt 5</div>
    <h2>Funnel Conversion-Raten</h2>
    <div class="sub">Ihre Conversion-Raten im Vergleich zum B2B-Benchmark — Stufe für Stufe.</div>
  </div>

  <table>
    <thead>
      <tr>
        <th>Funnel-Stufe</th>
        <th style="text-align:right;">Ihre Rate</th>
        <th style="text-align:right;">B2B Benchmark</th>
        <th>Bewertung</th>
        <th style="text-align:right;">Lücke (pp)</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Kontakt → Discovery Call</td>
        <td class="num ${rateClass(c2d, benchC2D)}">${fmtPct(c2d)}</td>
        <td class="num">~${benchC2D}%</td>
        <td class="${rateClass(c2d, benchC2D)}">${rateLabel(c2d, benchC2D)}</td>
        <td class="num">${c2d > 0 && c2d < benchC2D ? (benchC2D - c2d).toFixed(1) + ' pp' : '–'}</td>
      </tr>
      <tr>
        <td>Discovery → Präsentation / Angebot</td>
        <td class="num ${rateClass(d2p, benchD2P)}">${fmtPct(d2p)}</td>
        <td class="num">~${benchD2P}%</td>
        <td class="${rateClass(d2p, benchD2P)}">${rateLabel(d2p, benchD2P)}</td>
        <td class="num">${d2p > 0 && d2p < benchD2P ? (benchD2P - d2p).toFixed(1) + ' pp' : '–'}</td>
      </tr>
      <tr>
        <td>Präsentation → Abschluss (Closing)</td>
        <td class="num ${rateClass(p2d, benchP2D)}">${fmtPct(p2d)}</td>
        <td class="num">~${benchP2D}%</td>
        <td class="${rateClass(p2d, benchP2D)}">${rateLabel(p2d, benchP2D)}</td>
        <td class="num">${p2d > 0 && p2d < benchP2D ? (benchP2D - p2d).toFixed(1) + ' pp' : '–'}</td>
      </tr>
    </tbody>
  </table>

  ${bottleneck ? `
  <div class="alert-box blue">
    <strong>Primärer Engpass:</strong> ${safe(bottleneck)} — hier liegt die größte strukturelle Conversion-Lücke.
  </div>` : ''}

  <div class="kpi-grid-3">
    <div class="kpi-card">
      <div class="kpi-label">Monatliche Kontakte</div>
      <div class="kpi-val sm">${fmtNum(r.normalizedData?.monthlyContacts, 0)}</div>
      <div class="kpi-sub">Erfasste Erstkontakte/Monat</div>
    </div>
    <div class="kpi-card">
      <div class="kpi-label">Ø Deal-Größe</div>
      <div class="kpi-val sm">${fmtEur(r.normalizedData?.averageDealSize)}</div>
      <div class="kpi-sub">Durchschnittlicher Abschluss</div>
    </div>
    <div class="kpi-card">
      <div class="kpi-label">Sales Cycle</div>
      <div class="kpi-val sm">${fmtNum(r.normalizedData?.salesCycleMonths, 1)} Mo.</div>
      <div class="kpi-sub">Durchschn. Abschlusszeit</div>
    </div>
  </div>`;
}

// ─── Section 7: Zielpfad ──────────────────────────────────────────────────────
function buildGoalPath(r: MasterResult): string {
  const gp = r.computedMetrics?.goalPath;
  if (!gp || !gp.remainingRevenueNeeded) return '';

  const reps     = r.teamPerformance?.productiveReps || r.normalizedData?.teamSize || 1;
  const safeReps = Number(reps) > 0 ? Number(reps) : 1;

  const teamC  = gp.realisticRequiredContactsPerMonthFromNow ?? 0;
  const teamD  = gp.realisticRequiredDiscoveryCallsPerMonthFromNow ?? 0;
  const teamP  = gp.realisticRequiredPresentationsPerMonthFromNow ?? 0;
  const teamDeal = gp.requiredDealsPerMonthFromNow ?? 0;

  const repC    = Math.ceil(teamC / safeReps);
  const repD    = Math.ceil(teamD / safeReps);
  const repP    = Math.ceil(teamP / safeReps);
  const repDeal = (teamDeal / safeReps).toFixed(1);

  return `
  <div class="dark-header">
    <div class="dark-header-label">Abschnitt 6</div>
    <h2>Zielpfad — Was noch nötig ist</h2>
    <div class="sub">Operative Mindestanforderungen je Monat, um das Jahresziel rechnerisch noch zu erreichen.</div>
  </div>

  <div class="alert-box blue">
    Verbleibender Bedarf: <strong>${fmtEur(gp.remainingRevenueNeeded)}</strong> in ca.
    <strong>${gp.monthsRemainingApprox} Monaten</strong> · Konfidenz: <strong>${gp.confidenceLabel ?? '–'}</strong>
  </div>

  <table class="path-table">
    <thead>
      <tr>
        <th>Aktivität</th>
        <th style="text-align:right;">Team / Monat</th>
        <th style="text-align:right;">Je Rep / Monat</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Kontaktaufnahmen</td>
        <td class="num">${fmtNum(teamC, 0)}</td>
        <td class="num">${repC}</td>
      </tr>
      <tr>
        <td>Discovery Calls</td>
        <td class="num">${fmtNum(teamD, 0)}</td>
        <td class="num">${repD}</td>
      </tr>
      <tr>
        <td>Präsentationen / Angebote</td>
        <td class="num">${fmtNum(teamP, 0)}</td>
        <td class="num">${repP}</td>
      </tr>
      <tr style="background:#eff6ff;font-weight:700;">
        <td><strong>Abschlüsse (Deals)</strong></td>
        <td class="num"><strong>${fmtNum(teamDeal, 1)}</strong></td>
        <td class="num"><strong>${repDeal}</strong></td>
      </tr>
    </tbody>
  </table>
  <p style="font-size:7.5pt;color:#94a3b8;margin-top:4px;">
    Basis: Audit-adjustierte Conversion-Raten (C→D: ${fmtPct(gp.adjustedC2D)} | D→P: ${fmtPct(gp.adjustedD2P)} | P→Deal: ${fmtPct(gp.adjustedP2D)})
  </p>`;
}

// ─── Section 8: Unit Economics ────────────────────────────────────────────────
function buildUnitEconomics(r: MasterResult): string {
  const nd = r.normalizedData;
  const tp = r.teamPerformance;
  if (!nd || !tp) return '';

  const repCount = tp.productiveReps ?? nd.teamSize ?? 0;
  // Use data from unitEconomics block if available
  const ue = (tp as any).unitEconomics;
  const salaryTotal = ue?.totalSalaryCostYear ?? 0;
  const roiActual = ue?.revenuePerEuroSalary ?? 0;
  const avgDeal = nd.averageDealSize ?? 0;
  const salesCycle = nd.salesCycleMonths ?? 0;

  if (salaryTotal === 0 && avgDeal === 0 && salesCycle === 0) return '';

  return `
  <div class="dark-header">
    <div class="dark-header-label">Abschnitt 8</div>
    <h2>Unit Economics — Effizienz &amp; Wirtschaftlichkeit</h2>
    <div class="sub">Umsatz pro investiertem Euro Gehaltskosten und weitere Wirtschaftlichkeitskennzahlen.</div>
  </div>

  <div class="kpi-grid-4">
    ${avgDeal > 0 ? `<div class="kpi-card">
      <div class="kpi-label">&#216; Deal-Größe</div>
      <div class="kpi-val sm">${fmtEur(avgDeal)}</div>
      <div class="kpi-sub">Durchschnittlicher Abschluss</div>
    </div>` : ''}
    ${salesCycle > 0 ? `<div class="kpi-card">
      <div class="kpi-label">Sales Cycle</div>
      <div class="kpi-val sm">${fmtNum(salesCycle, 1)} Mo.</div>
      <div class="kpi-sub">&#216; Abschlusszeit</div>
    </div>` : ''}
    ${salaryTotal > 0 ? `<div class="kpi-card">
      <div class="kpi-label">Gehaltskosten / Jahr</div>
      <div class="kpi-val sm">${fmtEur(salaryTotal)}</div>
      <div class="kpi-sub">Alle ${repCount} produktiven Reps</div>
    </div>` : ''}
    ${roiActual > 0 ? `<div class="kpi-card ${roiActual < 4 ? 'alert' : roiActual >= 6 ? 'ok' : 'warn'}">
      <div class="kpi-label">Umsatz / &#8364; Gehalt (Ist)</div>
      <div class="kpi-val sm ${roiActual < 4 ? 'red' : roiActual >= 6 ? 'green' : 'amber'}">${roiActual.toFixed(2)} &#8364;</div>
      <div class="kpi-sub">B2B-Ziel: &#8805; 6,0 &#8364;</div>
    </div>` : ''}
  </div>

  ${roiActual > 0 && roiActual < 6 ? `<div class="alert-box amber">
    <strong>Effizienz unter Ziel:</strong> Pro Euro Gehaltskosten generiert das Team aktuell ${roiActual.toFixed(2)} &#8364; Umsatz.
    Der B2B-Benchmark für High-Performance-Teams liegt bei &#8805; 6,00 &#8364;. Hebel: Conversion-Optimierung und Pipeline-Skalierung.
  </div>` : roiActual >= 6 ? `<div class="alert-box green">
    <strong>Effizienz im Zielbereich:</strong> Das Team erreicht ${roiActual.toFixed(2)} &#8364; Umsatz pro Euro Gehaltskosten — im oberen B2B-Bereich.
  </div>` : ''}`;
}

// ─── Section 9: Datenlücken ────────────────────────────────────────────────────
function buildDataGaps(r: MasterResult): string {
  const gaps = safeArr(r.dataGaps);
  if (gaps.length === 0) return '';

  const pills = gaps.map((g: any) => {
    const label = g.field ?? g.title ?? 'Unbekannt';
    return `<span class="gap-pill">&#9888; ${safe(label)}</span>`;
  }).join('');

  return `
  <div class="dark-header page-break">
    <div class="dark-header-label">Abschnitt 9</div>
    <h2>Kritische Datenlücken</h2>
    <div class="sub">Diese Felder wurden als „Nicht bekannt" erfasst und schränken die Analysetiefe ein.</div>
  </div>
  <p style="margin-bottom:10px;">Folgende Datenpunkte konnten nicht erfasst werden. Eine präzisere Diagnose wird möglich, sobald diese Werte vorliegen:</p>
  <div style="margin-top:10px;margin-bottom:14px;">${pills}</div>
  <div class="alert-box amber">
    <strong>Empfehlung:</strong> Erfassen Sie die fehlenden Kennzahlen im nächsten Audit-Durchlauf — insbesondere Conversion-Raten und monatliche Kontaktvolumen — für eine signifikant präzisere Diagnose.
  </div>`;
}

// ─── Full HTML Builder ─────────────────────────────────────────────────────────
function buildResultsReportHtml(r: MasterResult): string {
  const companyName = r.normalizedData?.companyName || 'Ihr Unternehmen';

  return `<!DOCTYPE html>
<html lang="de">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <title>Salesforge Vertriebsdiagnose — ${companyName}</title>
  <style>${buildStyles()}</style>
  <script>
    window.addEventListener('load', function() {
      setTimeout(function() { window.print(); }, 700);
    });
  <\/script>
</head>
<body>
  <div class="no-print toolbar">
    <span class="toolbar-label">SALESFORGE · Vertriebsdiagnose · ${companyName}</span>
    <div class="toolbar-btns">
      <button class="btn-pdf" onclick="window.print()">&#128438; Als PDF speichern</button>
      <button class="btn-close" onclick="window.close()">&#x2715; Schließen</button>
    </div>
  </div>
  <div class="no-print toolbar-spacer"></div>

  ${buildCover(r)}
  <div class="slide"><div class="slide-content">
    ${buildBusinessReality(r)}
    <div class="report-footer">Salesforge Vertriebsdiagnose · ${companyName} · ${today()}</div>
  </div></div>
  <div class="slide"><div class="slide-content">
    ${buildStructuralCauses(r)}
    <div class="report-footer">Salesforge Vertriebsdiagnose · ${companyName} · ${today()}</div>
  </div></div>
  <div class="slide"><div class="slide-content">
    ${buildPerformancePace(r)}
    <div class="report-footer">Salesforge Vertriebsdiagnose · ${companyName} · ${today()}</div>
  </div></div>
  <div class="slide"><div class="slide-content">
    ${buildCapacityCheck(r)}
    <div class="report-footer">Salesforge Vertriebsdiagnose · ${companyName} · ${today()}</div>
  </div></div>
  <div class="slide"><div class="slide-content">
    ${buildFunnelConversions(r)}
    <div class="report-footer">Salesforge Vertriebsdiagnose · ${companyName} · ${today()}</div>
  </div></div>
  <div class="slide"><div class="slide-content">
    ${buildGoalPath(r)}
    <div class="report-footer">Salesforge Vertriebsdiagnose · ${companyName} · ${today()}</div>
  </div></div>
  <div class="slide"><div class="slide-content">
    ${buildTopLevers(r)}
    <div class="report-footer">Salesforge Vertriebsdiagnose · ${companyName} · ${today()}</div>
  </div></div>
  <div class="slide"><div class="slide-content">
    ${buildUnitEconomics(r)}
    <div class="report-footer">Salesforge Vertriebsdiagnose · ${companyName} · ${today()}</div>
  </div></div>
  <div class="slide"><div class="slide-content">
    ${buildDataGaps(r)}
    <div class="report-footer">Salesforge Vertriebsdiagnose · ${companyName} · ${today()}</div>
  </div></div>
</body>
</html>`;
}

// ─── Export ────────────────────────────────────────────────────────────────────
export function downloadResultsReport(result: MasterResult | null): void {
  if (!result) {
    alert('Keine Audit-Daten vorhanden. Bitte führen Sie zuerst das Audit durch.');
    return;
  }
  const html = buildResultsReportHtml(result);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const name = result.normalizedData?.companyName || 'Audit';
  const filename = `Salesforge_Diagnose_${name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}`;

  const w = window.open(url, '_blank');
  if (!w) {
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.html`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 300);
    return;
  }
  setTimeout(() => URL.revokeObjectURL(url), 300_000);
}